import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import swal from "@sweetalert/with-react";
import CustomeModal from "../component/modal-popup/customeModal";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { getTimeZoneForOnboarding } from "../redux/actions/onboarding";
import { userCheckVerificationCode, userVerification } from "../redux/actions/auth";

const schema = yup.object({
  first_name: yup.string().required("First name is required."),
  last_name: yup.string().required("Last name is required."),
  mobile: yup.string(),
  password: yup
    .string()
    .required("Password is required.")
    .min(6, "Password must be atleast 6 characters.")
    .max(15, "Password should not be greater than 15 characters.")
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,}$/,
      "Must contain 6 characters, one Uppercase, one Lowercase, one Number and one special case character"
    ),
  confirmPassword: yup
    .string()
    .required("Confirm password is required.")
    .min(6, "Password must be atleast 6 characters.")
    .max(15, "Password should not be greater than 15 characters.")
    .matches(
      /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{6,}$/,
      "Must contain 6 characters, one Uppercase, one Lowercase, one Number and one special case character"
    )
    .oneOf(
      [yup.ref("password"), null],
      "Password and Confirm Password must match"
    ),
  time_zone_id: yup
    .object()
    .shape({
      label: yup.string(),
      value: yup.string(),
    })
    .required("Time Zone is required.")
    .nullable(),
});

const disabledCss =
  "disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed";

const UserVerification = ({ user, setUser }) => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [isVerified, setIsVerified] = useState("");
  const [userData, setUserData] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user_id = searchParams.get("user_id");
  const verification_code = searchParams.get("verification_code");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { time_zone_for_onboarding } = useSelector((state) => state.onboarding);
  const [timeOptions, setTimeOptions] = useState([]);

  const { control, handleSubmit, reset, setValue } = useForm({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    setLoading(true);
    const payload = {
      user_id,
      verification_code,
    };

    dispatch(userCheckVerificationCode(payload, (res) => {
      if(res.status){
        setUserData(res.data);
        setLoading(false); 
        setIsVerified(true);
      }else{
        setLoading(false);
        setIsVerified(false);
      }
    }));

  }, []);

  useEffect(() => {
    const request = {
      onError: () => {
        swal({
          content: (
            <CustomeModal
              type="error"
              title="Error!"
              content="Something went wrong. Please try again later."
            />
          ),
          buttons: false,
          closeOnClickOutside: false,
          closeOnEsc: false,
        });
      },
    };

    dispatch(getTimeZoneForOnboarding(request));
  }, []);

  // Get time zone list
  useEffect(() => {
    if (time_zone_for_onboarding.length > 0) {
      const arr = [];
      time_zone_for_onboarding.map((element) => {
        const obj = {
          label: element.timezone,
          value: element.timezone_id,
        };
        arr.push(obj);
      });
      setTimeOptions(arr);
    }
  }, [time_zone_for_onboarding]);

  useEffect(() => {
    if (userData.time_zone_id) {
      const userTimeZone = time_zone_for_onboarding.find(
        (item) => item.timezone_id === userData.time_zone_id
      );

      if (userTimeZone) {
        setValue("time_zone_id", {
          label: userTimeZone.timezone,
          value: userTimeZone.timezone_id,
        });
      }
    } else {
      setValue("time_zone_id", null);
    }
  }, [userData]);

  const onSubmit = async (data) => {
    setButtonLoading(true);
    try {
      let payload = {
        ...data,
        user_id,
        verification_code,
        time_zone_id: data.time_zone_id?.value,
      };

      payload["name"] = `${data.first_name} ${data.last_name}`;

      dispatch(userVerification(payload, (res) => {
        if (res.status) {
          reset(defaultValues);
          swal({
            content: (
              <CustomeModal
                type="success"
                title="Success!"
                content={res.message}
                onClick={() => navigate("/login")}
              />
            ),
            buttons: false,
            closeOnClickOutside: false,
            closeOnEsc: false,
          });
        }
        setButtonLoading(false);
      }));
      
    } catch (error) {
      setButtonLoading(false);
    }
    reset(defaultValues);
  };

  const defaultValues = {
    first_name: "",
    last_name: "",
    password: "",
    confirmPassword: "",
    mobile: "",
  };

  return (
    <main className="bg-white">
      {(loading && (
        <div className="w-screen h-screen flex justify-center items-center">
          <span className="ml-2">
            <svg
              className="animate-spin w-20 h-20 fill-current shrink-0"
              viewBox="0 0 16 16"
            >
              <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
            </svg>
          </span>
        </div>
      )) || (
        <div className="flex">
          {/* Image */}
          <div className="hidden md:block md:w-1/2" aria-hidden="true">
            <img
              className="object-cover object-center w-full h-full"
              src={require("../assets/img/login-image.jpeg")}
              width="760"
              height="1024"
              alt="Authentication"
            />
          </div>
          <div className="relative h-screen md:flex grow">
            {/* Content */}
            <div className="md:w-full h-screen overflow-y-scroll">
              <div className="h-screen flex flex-col after:flex-1 overflow-y-auto">
                <div className="flex flex-col justify-center max-w-sm mx-auto px-4 py-8 h-screen overflow-y-auto">
                  {/* Header */}
                  <div className="flex mb-5">
                    <div className="flex items-center">
                      {/* Logo */}
                      <div className="block w-[40px]">
                        <img
                          alt="Logo"
                          src={require("../assets/img/ShiproneLogo.png")}
                        />
                      </div>
                    </div>
                  </div>
                  <h1 className="text-3xl text-slate-800 font-bold mb-6">
                    User Information
                  </h1>

                  {
                    isVerified ? (
                      <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="space-y-4 overflow-y-auto">
                          <Controller
                            name="first_name"
                            control={control}
                            defaultValue={
                              userData?.first_name
                                ? userData.first_name
                                : defaultValues.first_name
                            }
                            render={({ field, formState }) => (
                              <div>
                                <label
                                  className="block text-sm font-medium mb-1"
                                  htmlFor="name"
                                >
                                  First Name{" "}
                                  <span className="text-rose-500">*</span>
                                </label>
                                <input
                                  {...field}
                                  className="form-input w-full"
                                  type="text"
                                />
                                {formState.errors?.first_name && (
                                  <div className="text-xs text-rose-600 pt-1 pl-1">
                                    {formState.errors?.first_name?.message}
                                  </div>
                                )}
                              </div>
                            )}
                          />
                          <Controller
                            name="last_name"
                            control={control}
                            defaultValue={
                              userData?.last_name
                                ? userData.last_name
                                : defaultValues.last_name
                            }
                            render={({
                              field: { ref, ...field },
                              formState,
                            }) => (
                              <div>
                                <label
                                  className="block text-sm font-medium mb-1"
                                  htmlFor="name"
                                >
                                  Last Name{" "}
                                  <span className="text-rose-500">*</span>
                                </label>
                                <input
                                  {...field}
                                  className="form-input w-full"
                                  type="text"
                                />
                                {formState.errors?.last_name && (
                                  <div className="text-xs text-rose-600 pt-1 pl-1">
                                    {formState.errors?.last_name?.message}
                                  </div>
                                )}
                              </div>
                            )}
                          />
                          <Controller
                            name="mobile"
                            defaultValue={
                              userData?.mobile
                                ? userData.mobile
                                : defaultValues.mobile
                            }
                            control={control}
                            render={({ field, formState }) => (
                              <div>
                                <label
                                  className="block text-sm font-medium mb-1"
                                  htmlFor="company_name"
                                >
                                  Mobile Number{" "}
                                  <span className="text-rose-500">*</span>
                                </label>
                                <input
                                  {...field}
                                  className="form-input w-full"
                                  type="text"
                                />
                                {formState.errors?.mobile && (
                                  <div className="text-xs text-rose-600 pt-1 pl-1">
                                    {formState.errors?.mobile?.message}
                                  </div>
                                )}
                              </div>
                            )}
                          />
                          <div>
                            <label
                              className="block text-sm font-medium mb-1"
                              htmlFor="company_name"
                            >
                              Email
                            </label>
                            <input
                              value={userData?.email}
                              className="form-input w-full bg-slate-100 cursor-not-allowed"
                              type="text"
                              disabled
                            />
                          </div>

                          <Controller
                            name="time_zone_id"
                            control={control}
                            render={({ field, formState }) => (
                              <div>
                                <label
                                  htmlFor="country"
                                  class="block text-sm font-medium mb-1"
                                >
                                  Time Zone
                                  <span className="text-rose-500">*</span>
                                </label>
                                <Select
                                  name="time_zone_id"
                                  className="unresetText text-left"
                                  placeholder="Select Time Zone"
                                  menuPortalTarget={document.body}
                                  options={timeOptions}
                                  value={field.value}
                                  onChange={(val) => {
                                    setValue("time_zone_id", val);
                                  }}
                                  styles={{
                                    menuPortal: (base) => ({
                                      ...base,
                                      zIndex: 9999,
                                    }),
                                  }}
                                  classNames={{
                                    control: (style) => {
                                      return `!border !shadow-sm ${
                                        style.isFocused
                                          ? "!border-[--bg-color-300]/50"
                                          : "!border-slate-200 hover:!border-gray-300"
                                      }`;
                                    },
                                    container: (style) => {
                                      return "!h-[37.5px] !font-inter !text-sm";
                                    },
                                    menuList: (style) => {
                                      return `!max-h-[200px]`;
                                    },
                                  }}
                                />
                                {field.value === null &&
                                  formState.errors?.time_zone_id && (
                                    <div className="text-xs text-rose-600 pt-1 pl-1">
                                      {formState.errors?.time_zone_id?.message}
                                    </div>
                                  )}
                              </div>
                            )}
                          />
                          <Controller
                            name="password"
                            defaultValue={defaultValues.password}
                            control={control}
                            render={({ field, formState }) => (
                              <div>
                                <label
                                  className="block text-sm font-medium mb-1"
                                  htmlFor="password"
                                >
                                  Password{" "}
                                  <span className="text-rose-500">*</span>
                                </label>
                                <div className="relative">
                                  <input
                                    {...field}
                                    className="form-input w-full"
                                    type={showPassword ? "text" : "password"}
                                    autoComplete="off"
                                  />
                                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5">
                                    {showPassword && (
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke-width="1.5"
                                        stroke="currentColor"
                                        class="w-4 h-4 cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShowPassword(!showPassword);
                                        }}
                                      >
                                        <path
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                                        />
                                        <path
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                        />
                                      </svg>
                                    )}
                                    {!showPassword && (
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke-width="1.5"
                                        stroke="currentColor"
                                        class="w-4 h-4 cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShowPassword(!showPassword);
                                        }}
                                      >
                                        <path
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                          d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                                        />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                                {formState.errors?.password && (
                                  <div className="text-xs text-rose-600 pt-1 pl-1">
                                    {formState.errors?.password?.message}
                                  </div>
                                )}
                              </div>
                            )}
                          />

                          <Controller
                            name="confirmPassword"
                            defaultValue={defaultValues.confirmPassword}
                            control={control}
                            render={({ field, formState }) => (
                              <div>
                                <label
                                  className="block text-sm font-medium mb-1"
                                  htmlFor="confirmPassword"
                                >
                                  Confirm Password{" "}
                                  <span className="text-rose-500">*</span>
                                </label>
                                <div className="relative">
                                  <input
                                    {...field}
                                    className="form-input w-full"
                                    type={
                                      showConfirmPassword ? "text" : "password"
                                    }
                                    autoComplete="off"
                                  />
                                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center text-sm leading-5">
                                    {showConfirmPassword && (
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke-width="1.5"
                                        stroke="currentColor"
                                        class="w-4 h-4 cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShowConfirmPassword(
                                            !showConfirmPassword
                                          );
                                        }}
                                      >
                                        <path
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                          d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                                        />
                                        <path
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                        />
                                      </svg>
                                    )}
                                    {!showConfirmPassword && (
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke-width="1.5"
                                        stroke="currentColor"
                                        class="w-4 h-4 cursor-pointer"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShowConfirmPassword(
                                            !showConfirmPassword
                                          );
                                        }}
                                      >
                                        <path
                                          stroke-linecap="round"
                                          stroke-linejoin="round"
                                          d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                                        />
                                      </svg>
                                    )}
                                  </div>
                                </div>
                                {formState.errors?.confirmPassword && (
                                  <div className="text-xs text-rose-600 pt-1 pl-1">
                                    {formState.errors?.confirmPassword?.message}
                                  </div>
                                )}
                              </div>
                            )}
                          />
                        </div>
                        <div className="flex items-center justify-between mt-6">
                          <button
                            type="submit"
                            disabled={buttonLoading}
                            className={
                              "flex items-center flex-initial btn !bg-blue-500 hover:!bg-blue-600 text-white whitespace-nowrap w-full " +
                              (!buttonLoading ? "" : disabledCss)
                            }
                          >
                            Update
                            {buttonLoading && (
                              <span className="ml-2">
                                <svg
                                  className="animate-spin w-4 h-4 fill-current shrink-0"
                                  viewBox="0 0 16 16"
                                >
                                  <path d="M8 16a7.928 7.928 0 01-3.428-.77l.857-1.807A6.006 6.006 0 0014 8c0-3.309-2.691-6-6-6a6.006 6.006 0 00-5.422 8.572l-1.806.859A7.929 7.929 0 010 8c0-4.411 3.589-8 8-8s8 3.589 8 8-3.589 8-8 8z" />
                                </svg>
                              </span>
                            )}
                          </button>
                        </div>
                      </form>
                    ) : (
                      <div className="flex items-center flex-col py-8">
                        <div className="w-12 h-12 rounded-full bg-rose-600 text-white flex justify-center items-center">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke-width="1.5"
                            stroke="currentColor"
                            class="w-10 h-10"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                        </div>
                        <div className="font-md font-inter font-semibold py-3">
                          <span>
                            Your account is already verified or your
                            verification token is expired. Please contact to
                            administrator.
                          </span>
                        </div>
                        <Link
                          to="/login"
                          className="btn !bg-blue-500 hover:!bg-blue-600 text-white w-full mt-5"
                        >
                          Goto Login
                        </Link>
                      </div>
                    )
                  }
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default UserVerification;
